import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  HStack,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
} from '@enechain/ecloud-designsystem'

import { IntradayPreparingRadioItem } from '~/pages/IntradayBidding/components/IntradayPreparingOption'
import { INTRADAY_P8_AVAILABLE } from '~/pages/IntradayBidding/models/intradayP8Availability'

/** 入札価格の入れ方 (AC-BIN-005 / 006b / 007)。 */
export type IntradayPriceType = 'fixed' | 'formula'

type Props = {
  value: IntradayPriceType
  onChange: (next: IntradayPriceType) => void
  /** 表示専用の札 (下書き以外)。 呼び出し側が渡した種別を選んだ状態で、 選択肢をすべて非活性にする。 */
  readOnly?: boolean
}

// chakra v3 の `RadioGroupItem*Props` は d.ts 解決の問題で `children` / 固有 prop が欠落する。
// 札作成モーダルと同じく、 v3 本来の prop シグネチャへ cast して復元する (ランタイムは ecloud のまま)。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

/**
 * 入札価格の 固定価格 / フォーミュラ の切替。 成行を選べない入札編集・一括作成で使う
 * (札作成モーダルは成行を含む 3 択を自前で持つ)。 文言は札作成モーダルと同じ。
 *
 * フォーミュラは P8 の機能なので、 `INTRADAY_P8_AVAILABLE` が false の間は選択肢に出したまま
 * 非活性にし、 押せない理由「この機能は準備中です」 を名前側のツールチップで示す (AC-BIN-133)。
 */
export const IntradayPriceTypeRadio = ({
  value,
  onChange,
  readOnly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <RadioGroupRoot
      aria-label={t('page:biddingIntraday.form.priceType')}
      disabled={readOnly}
      onValueChange={(details: { value: string | null }) => {
        if (
          details.value === 'fixed' ||
          (INTRADAY_P8_AVAILABLE && details.value === 'formula')
        ) {
          onChange(details.value)
        }
      }}
      value={value}
    >
      <HStack flexWrap="wrap" gap={4}>
        <RadioGroupItem value="fixed">
          <RadioGroupItemHiddenInput />
          <RadioGroupItemControl />
          <RadioGroupItemText>
            {t('page:biddingIntraday.form.priceTypeFixed')}
          </RadioGroupItemText>
        </RadioGroupItem>
        <IntradayPreparingRadioItem
          label={t('page:biddingIntraday.form.priceTypeFormula')}
          value="formula"
        />
      </HStack>
    </RadioGroupRoot>
  )
}
